import { Component, Input } from '@angular/core';

import { DataPreview, formatBytes } from './data-preview';

/**
 * A pyst JSON response, shown as JSON.
 *
 * How much is parsed, how many rows are kept and how long the text may be are all
 * decided in `data-preview.ts`; this only draws what it is given, in one text node
 * so the DOM cost does not grow with the row count.
 */
@Component({
    selector: 'app-data-preview',
    template: `
        <div class="summary mono">
            <ng-container *ngIf="preview.rows as rows">
                {{ rows.shown }} of {{ rows.total }} row{{ rows.total === 1 ? '' : 's' }} &middot;
            </ng-container>
            {{ size }}
            <span class="capped" *ngIf="preview.note"> &middot; {{ preview.note }}</span>
        </div>
        <pre class="body mono">{{ preview.text }}<ng-container *ngIf="preview.truncated">
…truncated</ng-container></pre>
    `,
    styles: [
        `
            /*
             * Positioned against the host rather than laid out with flex: a
             * percentage height inside a flex chain resolves to a minimum often
             * enough that it is not worth relying on.
             */
            :host {
                display: block;
                position: relative;
                width: 100%;
                height: 100%;
                overflow: hidden;
            }

            .summary {
                position: absolute;
                top: 0;
                right: 0;
                left: 0;
                height: 24px;
                box-sizing: border-box;
                padding: 5px 8px;
                font-size: 11px;
                line-height: 14px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                color: rgba(0, 0, 0, 0.6);
                border-bottom: 1px solid rgba(0, 0, 0, 0.08);
            }

            .capped {
                color: #8a6d1d;
            }

            .body {
                position: absolute;
                top: 24px;
                right: 0;
                bottom: 0;
                left: 0;
                margin: 0;
                padding: 8px;
                overflow: auto;
                font-size: 11px;
                line-height: 1.5;
                tab-size: 2;
                white-space: pre;
                color: #1f2124;
            }

            .mono {
                font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
            }
        `,
    ],
})
export class DataPreviewComponent {
    @Input() preview!: DataPreview;
    /** Byte length of the response, for the summary line. */
    @Input() bytes = 0;

    get size(): string {
        return formatBytes(this.bytes);
    }
}
